import {
  sanitizeInlineStyleAttribute,
  sanitizeSimpleHtml,
} from './sanitizeSimpleHtml';

type LexicalNode = {
  type?: string;
  text?: string;
  format?: number | string;
  style?: string;
  textStyle?: string;
  listType?: string;
  children?: LexicalNode[];
};

type LexicalRoot = {
  root?: LexicalNode;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function stripStyleProperty(style: unknown, propertyName: string): string {
  if (typeof style !== 'string' || !style.trim()) return '';
  const normalizedPropertyName = propertyName.trim().toLowerCase();
  return style
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const [property] = part.split(':');
      return property.trim().toLowerCase() !== normalizedPropertyName;
    })
    .join('; ');
}

function parseLexicalRoot(value: unknown): LexicalRoot | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (!isRecord(parsed)) return null;
      const root = parsed.root;
      if (!isRecord(root) || root.type !== 'root' || !Array.isArray(root.children)) {
        return null;
      }
      return parsed as LexicalRoot;
    } catch {
      return null;
    }
  }
  if (!isRecord(value)) return null;
  const root = value.root;
  if (!isRecord(root) || root.type !== 'root' || !Array.isArray(root.children)) {
    return null;
  }
  return value as LexicalRoot;
}

function stripInlineFontFamilyFromNode(node: LexicalNode): LexicalNode {
  const nextNode: LexicalNode = { ...node };
  if (typeof nextNode.style === 'string') {
    nextNode.style = stripStyleProperty(nextNode.style, 'font-family');
  }
  if (typeof nextNode.textStyle === 'string') {
    nextNode.textStyle = stripStyleProperty(nextNode.textStyle, 'font-family');
  }
  if (Array.isArray(nextNode.children)) {
    nextNode.children = nextNode.children.map((child) => stripInlineFontFamilyFromNode(child));
  }
  return nextNode;
}

function normalizeFormat(format: unknown): { bold: boolean; italic: boolean } {
  if (typeof format === 'number' && Number.isFinite(format)) {
    const normalized = Math.round(format);
    return {
      bold: (normalized & 1) !== 0,
      italic: (normalized & 2) !== 0,
    };
  }
  if (typeof format === 'string') {
    const tokens = format.toLowerCase().split(/[\s,|]+/).filter(Boolean);
    return {
      bold: tokens.includes('bold'),
      italic: tokens.includes('italic'),
    };
  }
  return { bold: false, italic: false };
}

function plainTextFromNode(node: LexicalNode): string {
  if (node.type === 'text') return String(node.text ?? '');
  if (node.type === 'linebreak') return '\n';
  const children = Array.isArray(node.children) ? node.children : [];
  return children.map((child) => plainTextFromNode(child)).join('');
}

function htmlFromNode(node: LexicalNode): string {
  if (node.type === 'text') {
    const text = escapeHtml(String(node.text ?? ''));
    const { bold, italic } = normalizeFormat(node.format);
    const inlineStyle = sanitizeInlineStyleAttribute(
      String(node.style ?? node.textStyle ?? '')
    );
    let formatted = text;
    if (bold) formatted = `<strong>${formatted}</strong>`;
    if (italic) formatted = `<em>${formatted}</em>`;
    if (inlineStyle) {
      formatted = `<span style="${escapeHtml(inlineStyle)}">${formatted}</span>`;
    }
    return formatted;
  }

  if (node.type === 'linebreak') return '<br />';

  const children = Array.isArray(node.children) ? node.children : [];
  const childrenHtml = children.map((child) => htmlFromNode(child)).join('');

  if (node.type === 'paragraph' || node.type === 'heading') {
    return `<p>${childrenHtml}</p>`;
  }
  if (node.type === 'list') {
    const tag = node.listType === 'number' ? 'ol' : 'ul';
    return `<${tag}>${childrenHtml}</${tag}>`;
  }
  if (node.type === 'listitem') {
    return `<li>${childrenHtml}</li>`;
  }

  return childrenHtml;
}

export function lexicalPlainTextToSerializedState(value: string): string {
  const normalized = value.replace(/\r\n/g, '\n');
  const paragraphs = normalized.split('\n');
  const paragraphNodes = paragraphs.map((line) => ({
    type: 'paragraph',
    children: line
      ? [
          {
            type: 'text',
            text: line,
            format: 0,
            style: '',
            detail: 0,
            mode: 'normal',
            version: 1,
          },
        ]
      : [],
    direction: null,
    format: '',
    indent: 0,
    version: 1,
    textFormat: 0,
    textStyle: '',
  }));

  return JSON.stringify({
    root: {
      type: 'root',
      children: paragraphNodes,
      direction: null,
      format: '',
      indent: 0,
      version: 1,
    },
  });
}

export function coerceLexicalStateString(value: unknown, fallbackPlainText = ''): string {
  const parsed = parseLexicalRoot(value);
  if (parsed) return JSON.stringify(parsed);
  return lexicalPlainTextToSerializedState(fallbackPlainText);
}

export function stripLexicalInlineFontFamily(value: unknown, fallbackPlainText = ''): string {
  const parsed = parseLexicalRoot(value);
  if (!parsed?.root) {
    return lexicalPlainTextToSerializedState(
      typeof value === 'string' && value.trim() ? value : fallbackPlainText
    );
  }
  const children = Array.isArray(parsed.root.children)
    ? parsed.root.children.map((node) => stripInlineFontFamilyFromNode(node))
    : [];
  return JSON.stringify({
    ...parsed,
    root: {
      ...parsed.root,
      children,
    },
  });
}

export function lexicalStateToPlainText(value: unknown): string {
  const parsed = parseLexicalRoot(value);
  if (!parsed?.root) {
    return typeof value === 'string' ? value : '';
  }
  const children = Array.isArray(parsed.root.children) ? parsed.root.children : [];
  const chunks = children
    .map((node) => plainTextFromNode(node))
    .map((chunk) => chunk.trim())
    .filter(Boolean);
  return chunks.join('\n\n');
}

export function lexicalStateToHtml(value: unknown): string {
  const parsed = parseLexicalRoot(value);
  if (!parsed?.root) {
    return typeof value === 'string' ? escapeHtml(value) : '';
  }
  const children = Array.isArray(parsed.root.children) ? parsed.root.children : [];
  return children.map((node) => htmlFromNode(node)).join('');
}

export function lexicalStateToSanitizedHtml(value: unknown): string {
  return sanitizeSimpleHtml(lexicalStateToHtml(value));
}
